/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loading from 'components/Loading';
import Button from 'components/Buttons/Button';
import InputText from 'components/Inputs/inputText';
import InputDate from 'components/Inputs/inputDate';
import InputSelect from 'components/Inputs/inputSelect';
import InputFile from 'components/Inputs/inputFile';
import InputRichText from 'components/Inputs/inputRichText';
import OutputValue from 'components/Inputs/outputValue';
import Alert from 'components/Alert';
import { useParams, useSearchParams } from 'react-router-dom';
import { SquareCheck, Square } from 'lucide-react';
import { formatDate, formatNumber } from 'utils/functions';
import { fetchCaptableShares } from '../modules/actions';
import ShareClassAssignments from './ShareClassAssignments';

function FormChangeClass(props) {
  const {
    type,
    data,
    dateOrID,
    id,
    literals,
    literalsCommon,
    captable,
    currency,
    onConfirm,
    onDelete,
    editable,
  } = props;

  const [ident] = useState(data.id ?? `new.${new Date().getTime()}`);
  const [name, setName] = useState(data?.name);
  const [shareholders, setShareholders] = useState();
  const [inputDate, setInputDate] = useState(null);
  const [issuingShareholder, setIssuingShareholder] = useState(id || data?.shareholder);
  const [selectedShares, setSelectedShares] = useState(data.shares || {});
  const [shareholderShares, setShareholderShares] = useState();
  const [notes, setNotes] = useState(data.notes);
  const [documents, setDocuments] = useState([]);
  const [searchParams] = useSearchParams();
  const captableId = searchParams.get('captable') || null;
  const params = useParams();

  // Get all shareholders
  useEffect(() => {
    (async () => {
      const auxDate = dateOrID || formatDate(new Date('2035-12-31'), { format: 'Y-m-d' });
      const shareHolders = await fetchCaptableShares(
        params.id,
        auxDate,
        { group: 'shareholder', size: 0, captable: captableId },
      );

      setShareholders(shareHolders.items.map(sh => ({
        id: sh.shareholder.id,
        name: `${sh.shareholder.name} (${formatNumber(sh.number)} ${literals.shares})`,
      })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get all the shares of a shareholder
  useEffect(() => {
    if (issuingShareholder) {
      const auxDate = dateOrID || formatDate(new Date('2035-12-31'), { format: 'Y-m-d' });
      (async () => {
        const response = await fetchCaptableShares(
          params.id,
          auxDate,
          { size: 0, filters: { shareholder: issuingShareholder }, captable: captableId },
        );

        // Group by classes
        let shShares = [];
        response.items.forEach((item) => {
          let shShare = shShares[item.shareClass.id];
          if (!shShare) {
            shShare = {
              count: 0,
              capital: 0,
              pps: 0,
              shareClass: item.shareClass.id,
              number: 0,
              numeration: [],
              from: 0,
              to: 0,
              info: item.shareClass,
            };
          }
          shShare.count += 1;
          shShare.capital += item.capital;
          shShare.number += item.number;
          shShare.pps += item.number * item.pps;
          shShare.numeration.push(item.numeration);
          if (!shShare.from || item.numeration.from < shShare.from) {
            shShare.from = item.numeration.from;
          }
          if (!shShare.to || item.numeration.to < shShare.to) {
            shShare.to = item.numeration.to;
          }
          shShares[item.shareClass.id] = shShare;
        });

        shShares = Object.values(shShares).map((share) => ({ ...share, pps: parseFloat(share.pps / share.number) }));

        setShareholderShares(shShares);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuingShareholder]);

  const handleSelectedShares = (item) => {
    const newSelectedShares = { ...selectedShares };
    if (newSelectedShares[item.shareClass]) {
      delete (newSelectedShares[item.shareClass]);
    } else {
      newSelectedShares[item.shareClass] = {
        shareClass: item.shareClass,
        changes: [],
      };
    }
    setSelectedShares(newSelectedShares);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (type === 'operation') {
      onConfirm({
        id: ident,
        shareholder: issuingShareholder,
        shares: selectedShares,
      });
    } else {
      onConfirm({
        operationType: 'changeClasses',
        data: {
          id: ident,
          shareholder: issuingShareholder,
          shares: selectedShares,
        },
        name,
        date: inputDate,
        notes,
        documents,
        shareholder: issuingShareholder,
      });
    }
  };

  const handleDeleteForm = () => {
    onDelete(ident);
  };

  const numerations = {};
  shareholderShares?.forEach((share) => {
    numerations[share.info.id] = share.numeration;
  });

  let errorRange = null;
  Object.values(selectedShares).forEach((share) => {
    share.changes.forEach((change) => {
      if (change.number > 0 && change?.from > 0) {
        const findNumeration = numerations[share.shareClass]?.find(s => (
          change.from >= s.from
          && change.to <= s.to
        ));
        if (!findNumeration) {
          errorRange = { shareClass: share.shareClass, numeration: { from: change.from, to: change.to } };
        }
      }
    });
  });

  return (
    <>
      {type !== 'operation' && (
        <>
          <div className='row'>
            <div className='col-12 col-md-8'>
              <InputText
                className='m-0'
                preText={literals.operationName}
                placeholder={literals.operationName}
                value={name}
                onChange={v => setName(v)}
                isDisabled={!editable}
              />
            </div>
            <div className='col-12 col-md-4'>
              <InputDate
                className='m-0'
                preText={literalsCommon.date}
                value={inputDate}
                onChange={v => setInputDate(v)}
                isDisabled={!editable}
                min={captable?.operations?.length ? captable?.operations[0].date : null}
              />
            </div>
          </div>
          <hr />
        </>
      )}
      <div className={`row ${type === 'operation' && 'row mb-4'}`}>
        {type !== 'operation' ? (
          <div className='col-12 col-md-6'>
            <OutputValue
              preText={literals.shareholder}
              value={captable.shareholders.find(item => item.id === issuingShareholder)?.name || '-'}
            />
          </div>
        ) : (
          <>
            {shareholders ? (
              <div className='col-12 col-md-6'>
                <InputSelect
                  options={shareholders}
                  className='mb-md-0'
                  preText={literals.shareholder}
                  value={issuingShareholder}
                  onChange={(v) => {
                    setIssuingShareholder(v);
                    setShareholderShares(null);
                    setSelectedShares({});
                  }}
                  isDisabled={!editable}
                />
              </div>
            ) : (
              <Loading mode='panel' />
            )}
          </>
        )}
      </div>
      {
        shareholderShares ? (
          shareholderShares.map(item => (
            <div key={item.shareClass} className='box box-padding box-mb '>
              <div
                className='d-flex justify-content-start align-items-center'
                onClick={editable ? () => handleSelectedShares(item) : null}
                style={editable ? { cursor: 'pointer' } : {}}
              >
                {selectedShares[item.shareClass] ? (<SquareCheck size={18} />) : (<Square size={18} />)}
                <h5 className='fw-b mb-0 ps-3'>
                  {`${formatNumber(item.number)}# ${item.info.name} (${item.info.code || '-'})`}
                </h5>
              </div>
              {
                selectedShares[item.shareClass] ? (
                  <>
                    <hr />
                    <div className='row mb-2'>
                      <div className='col-12 col-md-4'>
                        <h6 className='fw-b'>{literals.number}</h6>
                        <p>{formatNumber(item.number) || '-'}</p>
                      </div>
                      <div className='col-12 col-md-4'>
                        <h6 className='fw-b'>{literals.capital}</h6>
                        <p>{formatNumber(item.capital) || '-'}</p>
                      </div>
                      <div className='col-12 col-md-4'>
                        <h6 className='fw-b'>{literals.pps}</h6>
                        <p>{`${item.count > 1 ? '~' : ''}${formatNumber(item.pps, 0, { symbol: currency.symbol })}`}</p>
                      </div>
                      <div className='col-12'>
                        <h6 className='fw-b'>{item.count > 1 ? literals.numerations : literals.numeration}</h6>
                        <p>
                          {item.numeration && item.numeration.map(num => (`${formatNumber(num.from)} - ${formatNumber(num.to)}`)).join(',')}
                        </p>
                      </div>
                    </div>
                    <ShareClassAssignments
                      id={item.shareClass}
                      number={item.number}
                      numeration={item.numeration[0]}
                      pps={parseFloat(item.pps)}
                      captable={{ ...captable, shareClasses: captable.shareClasses.filter(sc => sc.id !== item.shareClass) }}
                      currency={currency}
                      literals={literals}
                      literalsCommon={literalsCommon}
                      assignments={selectedShares[item.shareClass].changes}
                      onChange={(v) => {
                        setSelectedShares((prev) => {
                          const aux = { ...prev };
                          aux[item.shareClass].changes = v;
                          return aux;
                        });
                      }}
                      editable={editable}
                    />
                    {
                      errorRange?.shareClass === item.shareClass && (
                        <Alert
                          type='danger'
                          text={`${literals.errors.invalidRange}: ${errorRange.numeration.from} - ${errorRange.numeration.to}`}
                          className='mt-4'
                        />
                      )
                    }
                  </>
                ) : (null)
              }
            </div>
          ))
        ) : (
          <>
            {issuingShareholder && <Loading mode='panel' />}
          </>
        )
      }
      {type !== 'operation' && (
        <>
          <hr />
          <div className='row'>
            <div className='col-12 '>
              <InputRichText
                minHeight='120px'
                preText={literals.additionalDocumentation}
                value={notes}
                onChange={v => setNotes(v)}
                isDisabled={!editable}
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-12'>
              <InputFile
                preText={literalsCommon.documents}
                value={documents}
                isPublic={false}
                onChange={v => setDocuments(v)}
                multiple
                isDisabled={!editable}
              />
            </div>
          </div>
        </>
      )}
      { editable && (
        <div className='buttons'>
          <Button onClick={submitForm} text={literalsCommon.confirm} />
          { type === 'operation' && data.id && (
            <Button
              type='button'
              color='danger'
              className='me-3'
              text={literalsCommon.delete}
              onClick={handleDeleteForm}
            />
          )}
        </div>
      )}
    </>
  );
}

FormChangeClass.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object,
  literals: PropTypes.object.isRequired,
  literalsCommon: PropTypes.object.isRequired,
  captable: PropTypes.object.isRequired,
  currency: PropTypes.object.isRequired,
  type: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  editable: PropTypes.bool,
  dateOrID: PropTypes.string,
};

FormChangeClass.defaultProps = {
  id: '',
  type: '',
  data: {},
  editable: true,
  dateOrID: formatDate(new Date('2035-12-31'), { format: 'Y-m-d' }),
  onDelete: () => {},
};

export default FormChangeClass;
